// src/components/issues/AdminIssueThresholdModal.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  Button, FormControl, FormLabel, Input, VStack, NumberInput, NumberInputField,
  NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper, Checkbox, Text,
  useColorModeValue, FormHelperText, Alert, AlertIcon, AlertDescription, HStack,
  Menu, MenuButton, MenuList, MenuItem, Icon, Slider, SliderTrack, SliderFilledTrack,
  SliderThumb, Box, Badge, Tooltip, Divider, SimpleGrid, Switch, AlertTitle
} from '@chakra-ui/react';
import { MdSave, MdRefresh, MdSettings, MdAutoAwesome, MdSpeed, MdWarning } from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';

// ============================================================================
// MOTION COMPONENTS
// ============================================================================
const MotionBox = motion(Box);
const MotionSimpleGrid = motion(SimpleGrid);

// ============================================================================
// ANIMATION VARIANTS
// ============================================================================
const slideUpVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 25 }
  }
};

const scaleVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: { duration: 0.3 }
  }
};

const presetVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { delay: i * 0.1, duration: 0.3 }
  }),
  hover: { 
    scale: 1.05, 
    y: -2,
    transition: { duration: 0.2 } 
  }
};

const DEBUG_CONTEXT = '[AdminIssueThresholdModal]';

const logDebug = (message, data = null) => {
  const timestamp = new Date().toISOString();
  console.log(`${timestamp} ${DEBUG_CONTEXT} ${message}`, data || '');
};

const PRESET_CONFIGURATIONS = {
  conservative: {
    name: 'Conservative',
    description: 'High threshold for issue generation',
    minReports: 10,
    radiusMeters: 50,
    timeWindowHours: 12,
    autoGenerateIssueType: 'system_detected_cluster',
    autoAssignWorkOrder: false,
  },
  moderate: {
    name: 'Moderate',
    description: 'Balanced threshold settings',
    minReports: 5,
    radiusMeters: 100,
    timeWindowHours: 24,
    autoGenerateIssueType: 'system_detected_cluster',
    autoAssignWorkOrder: true,
  },
  aggressive: {
    name: 'Aggressive',
    description: 'Low threshold for early detection',
    minReports: 3,
    radiusMeters: 200,
    timeWindowHours: 48,
    autoGenerateIssueType: 'system_detected_cluster',
    autoAssignWorkOrder: true,
  },
};

const AdminIssueThresholdModal = ({ isOpen, onClose, onSave, currentConfig, isLoading }) => {
  const defaultConfig = useMemo(() => ({
    minReports: 5,
    radiusMeters: 100,
    timeWindowHours: 24,
    autoGenerateIssueType: 'system_detected_cluster',
    autoAssignWorkOrder: true,
    enableSmartClustering: false,
    minSimilarityScore: 0.6,
    autoNotifyUsers: true,
  }), []);

  const [config, setConfig] = useState(defaultConfig);
  const [errors, setErrors] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(null);
  
  const modalBg = useColorModeValue("white", "navy.800");
  const inputBg = useColorModeValue("gray.50", "navy.700");
  const labelColor = useColorModeValue("gray.700", "gray.200");
  const presetBg = useColorModeValue("gray.100", "navy.600");
  const footerBg = useColorModeValue("gray.50", "navy.900");
  const gradientBg = useColorModeValue(
    'linear(to-r, blue.400, purple.500)',
    'linear(to-r, blue.600, purple.700)'
  );

  useEffect(() => {
    if (isOpen) {
      const initialConfig = currentConfig || defaultConfig;
      logDebug('Modal opened', { initialConfig });
      setConfig(initialConfig);
      setErrors({});
      setHasChanges(false);
      setSelectedPreset(null);
    }
  }, [currentConfig, isOpen, defaultConfig]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    logDebug('Config field changed', { name, value: type === 'checkbox' ? checked : value });
    
    setConfig(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
    
    setHasChanges(true);
    setSelectedPreset(null);
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleNumberChange = (name, valueAsString, valueAsNumber) => {
    const value = isNaN(valueAsNumber) ? 0 : valueAsNumber;
    
    logDebug('Number field changed', { name, value });
    
    setConfig(prev => ({ ...prev, [name]: value }));
    setHasChanges(true);
    setSelectedPreset(null);
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSliderChange = (name, value) => {
    logDebug('Slider changed', { name, value });
    
    setConfig(prev => ({ ...prev, [name]: value }));
    setHasChanges(true);
    setSelectedPreset(null);
  };

  const validateConfig = () => {
    const newErrors = {};
    
    if (config.minReports < 2) {
      newErrors.minReports = 'Minimum reports must be at least 2';
    } else if (config.minReports > 100) {
      newErrors.minReports = 'Minimum reports cannot exceed 100';
    }
    
    if (config.radiusMeters < 10) {
      newErrors.radiusMeters = 'Radius must be at least 10 meters';
    } else if (config.radiusMeters > 1000) {
      newErrors.radiusMeters = 'Radius cannot exceed 1000 meters';
    }
    
    if (config.timeWindowHours < 1) {
      newErrors.timeWindowHours = 'Time window must be at least 1 hour';
    } else if (config.timeWindowHours > 168) {
      newErrors.timeWindowHours = 'Time window cannot exceed 168 hours (1 week)';
    }
    
    if (!config.autoGenerateIssueType || config.autoGenerateIssueType.trim() === '') {
      newErrors.autoGenerateIssueType = 'Issue type is required';
    }

    if (config.enableSmartClustering) {
      if (config.minSimilarityScore < 0 || config.minSimilarityScore > 1) {
        newErrors.minSimilarityScore = 'Similarity score must be between 0 and 1';
      }
    }
    
    logDebug('Validation result', { 
      isValid: Object.keys(newErrors).length === 0, 
      errors: newErrors 
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    logDebug('Submit initiated', { config });
    
    if (!validateConfig()) {
      logDebug('Validation failed');
      return;
    }
    
    const finalConfig = {
      ...config,
      minReports: parseInt(config.minReports, 10),
      radiusMeters: parseInt(config.radiusMeters, 10),
      timeWindowHours: parseInt(config.timeWindowHours, 10),
      minSimilarityScore: parseFloat(config.minSimilarityScore),
    };
    
    logDebug('Submitting validated config', { finalConfig });
    
    onSave(finalConfig);
  };

  const handleLoadPreset = (presetKey) => {
    const preset = PRESET_CONFIGURATIONS[presetKey];
    
    logDebug('Loading preset', { presetKey, preset });
    
    setConfig(preset);
    setSelectedPreset(presetKey);
    setHasChanges(true);
    setErrors({});
  };

  const handleReset = () => {
    logDebug('Resetting to default config', { defaultConfig });
    
    setConfig(defaultConfig);
    setSelectedPreset(null);
    setHasChanges(true);
    setErrors({});
  };

  const estimatedImpact = useMemo(() => {
    const score = (config.minReports * 0.4) + 
                  (config.radiusMeters / 1000 * 0.3) + 
                  (config.timeWindowHours / 168 * 0.3);
    
    if (score < 0.3) return { level: 'High', color: 'red', description: 'Will generate many automatic issues' };
    if (score < 0.6) return { level: 'Medium', color: 'orange', description: 'Balanced issue generation' };
    return { level: 'Low', color: 'green', description: 'Conservative issue generation' };
  }, [config]);

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size="xl" 
      scrollBehavior="inside"
      motionPreset="slideInBottom"
    >
      <ModalOverlay backdropFilter="blur(6px)" bg="blackAlpha.600" />
      <ModalContent 
        bg={modalBg} 
        maxH="95vh" 
        borderRadius="2xl" 
        overflow="hidden"
      >
        <form onSubmit={handleSubmit}>
          {/* Animated gradient header */}
          <MotionBox
            h="4px"
            bgGradient={gradientBg}
            animate={{
              backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "linear"
            }}
            style={{
              backgroundSize: '200% 200%'
            }}
          />

          <ModalHeader borderBottomWidth="1px">
            <HStack spacing={3}>
              <MotionBox
                animate={{
                  rotate: [0, 360],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "linear"
                }}
              >
                <Icon as={MdSettings} boxSize={6} color="blue.500" />
              </MotionBox>
              <VStack align="start" spacing={0}>
                <Text fontSize="lg" fontWeight="800">Configure Automatic Issue Generation</Text>
                <Text fontSize="xs" color="gray.500" fontWeight="normal">
                  Set thresholds for clustering and auto-generation
                </Text>
              </VStack>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          
          <ModalBody pb={6} overflowY="auto">
            <VStack spacing={6} align="stretch">
              <MotionBox variants={scaleVariants} initial="hidden" animate="visible">
                <Alert status="info" borderRadius="xl" variant="left-accent" boxShadow="sm">
                  <AlertIcon />
                  <AlertDescription fontSize="sm">
                    Define thresholds for automatically generating a new issue when multiple similar user reports occur within a specific area and timeframe.
                  </AlertDescription>
                </Alert>
              </MotionBox>

              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={3} color={labelColor}>
                  Quick Presets
                </Text>
                <MotionSimpleGrid 
                  columns={3} 
                  spacing={3}
                  initial="hidden"
                  animate="visible"
                >
                  {Object.entries(PRESET_CONFIGURATIONS).map(([key, preset], index) => (
                    <MotionBox
                      key={key}
                      custom={index}
                      variants={presetVariants}
                      whileHover="hover"
                    >
                      <Button
                        size="sm"
                        variant={selectedPreset === key ? 'solid' : 'outline'}
                        colorScheme={selectedPreset === key ? 'blue' : 'gray'}
                        onClick={() => handleLoadPreset(key)}
                        p={3}
                        h="auto"
                        flexDirection="column"
                        alignItems="stretch"
                        borderRadius="xl"
                        boxShadow={selectedPreset === key ? 'md' : 'sm'}
                        transition="all 0.2s"
                      >
                        <Text fontWeight="bold" fontSize="xs">{preset.name}</Text>
                        <Text fontSize="2xs" color="gray.500" mt={1}>{preset.description}</Text>
                      </Button>
                    </MotionBox>
                  ))}
                </MotionSimpleGrid>
              </Box>

              <Divider />

              <MotionBox variants={slideUpVariants} initial="hidden" animate="visible">
                <FormControl isRequired isInvalid={!!errors.minReports}>
                  <FormLabel color={labelColor} fontWeight="600">Minimum Reports</FormLabel>
                  <HStack>
                    <NumberInput 
                      name="minReports" 
                      value={config.minReports} 
                      onChange={(valStr, valNum) => handleNumberChange('minReports', valStr, valNum)} 
                      min={2} 
                      max={100}
                      flex={1}
                    >
                      <NumberInputField bg={inputBg} borderRadius="lg" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <Badge colorScheme="blue" px={4} py={2} borderRadius="lg" fontSize="sm">
                      {config.minReports} reports
                    </Badge>
                  </HStack>
                  {errors.minReports ? (
                    <FormHelperText color="red.500">{errors.minReports}</FormHelperText>
                  ) : (
                    <FormHelperText>Number of user reports to trigger auto-generation.</FormHelperText>
                  )}
                </FormControl>
              </MotionBox>

              <MotionBox 
                variants={slideUpVariants} 
                initial="hidden" 
                animate="visible"
                transition={{ delay: 0.1 }}
              >
                <FormControl isRequired isInvalid={!!errors.radiusMeters}>
                  <FormLabel color={labelColor} fontWeight="600">Radius (meters)</FormLabel>
                  <VStack align="stretch" spacing={3}>
                    <Slider
                      value={config.radiusMeters}
                      onChange={(val) => handleSliderChange('radiusMeters', val)}
                      min={10}
                      max={1000}
                      step={10}
                    >
                      <SliderTrack bg="gray.200" borderRadius="full">
                        <SliderFilledTrack bg="blue.500" />
                      </SliderTrack>
                      <SliderThumb boxSize={6} boxShadow="lg">
                        <Box color="blue.500" />
                      </SliderThumb>
                    </Slider>
                    <HStack justify="space-between">
                      <Text fontSize="xs" color="gray.500" fontWeight="600">10m</Text>
                      <Badge colorScheme="blue" fontSize="md" px={4} py={1} borderRadius="lg">
                        {config.radiusMeters}m
                      </Badge>
                      <Text fontSize="xs" color="gray.500" fontWeight="600">1000m</Text>
                    </HStack>
                  </VStack>
                  {errors.radiusMeters ? (
                    <FormHelperText color="red.500">{errors.radiusMeters}</FormHelperText>
                  ) : (
                    <FormHelperText>Spatial proximity for clustering reports.</FormHelperText>
                  )}
                </FormControl>
              </MotionBox>

              <MotionBox 
                variants={slideUpVariants} 
                initial="hidden" 
                animate="visible"
                transition={{ delay: 0.2 }}
              >
                <FormControl isRequired isInvalid={!!errors.timeWindowHours}>
                  <FormLabel color={labelColor} fontWeight="600">Time Window (hours)</FormLabel>
                  <VStack align="stretch" spacing={3}>
                    <Slider
                      value={config.timeWindowHours}
                      onChange={(val) => handleSliderChange('timeWindowHours', val)}
                      min={1}
                      max={168}
                      step={1}
                    >
                      <SliderTrack bg="gray.200" borderRadius="full">
                        <SliderFilledTrack bg="purple.500" />
                      </SliderTrack>
                      <SliderThumb boxSize={6} boxShadow="lg">
                        <Box color="purple.500" />
                      </SliderThumb>
                    </Slider>
                    <HStack justify="space-between">
                      <Text fontSize="xs" color="gray.500" fontWeight="600">1h</Text>
                      <Badge colorScheme="purple" fontSize="md" px={4} py={1} borderRadius="lg">
                        {config.timeWindowHours}h ({(config.timeWindowHours / 24).toFixed(1)}d)
                      </Badge>
                      <Text fontSize="xs" color="gray.500" fontWeight="600">168h (7d)</Text>
                    </HStack>
                  </VStack>
                  {errors.timeWindowHours ? (
                    <FormHelperText color="red.500">{errors.timeWindowHours}</FormHelperText>
                  ) : (
                    <FormHelperText>Temporal proximity for clustering reports.</FormHelperText>
                  )}
                </FormControl>
              </MotionBox>

              <MotionBox 
                variants={slideUpVariants} 
                initial="hidden" 
                animate="visible"
                transition={{ delay: 0.3 }}
              >
                <FormControl isInvalid={!!errors.autoGenerateIssueType}>
                  <FormLabel color={labelColor} fontWeight="600">Type for Auto-Generated Issue</FormLabel>
                  <Input 
                    name="autoGenerateIssueType" 
                    value={config.autoGenerateIssueType} 
                    onChange={handleChange} 
                    placeholder="e.g., system_detected_cluster"
                    bg={inputBg}
                    borderRadius="lg"
                  />
                  {errors.autoGenerateIssueType ? (
                    <FormHelperText color="red.500">{errors.autoGenerateIssueType}</FormHelperText>
                  ) : (
                    <FormHelperText>The issue type assigned to automatically generated issues.</FormHelperText>
                  )}
                </FormControl>
              </MotionBox>

              <Divider />

              <Text fontSize="sm" fontWeight="bold" color={labelColor}>
                Advanced Options
              </Text>

              <MotionBox 
                variants={slideUpVariants} 
                initial="hidden" 
                animate="visible"
                transition={{ delay: 0.4 }}
              >
                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <Box flex="1" mr={4}>
                    <FormLabel htmlFor="autoAssignWorkOrder" mb="1" fontWeight="600">
                      Auto-Create Work Orders
                    </FormLabel>
                    <FormHelperText mt={0} fontSize="xs">
                      Automatically create & assign work orders for auto-generated issues
                    </FormHelperText>
                  </Box>
                  <Switch 
                    id="autoAssignWorkOrder"
                    name="autoAssignWorkOrder" 
                    isChecked={config.autoAssignWorkOrder} 
                    onChange={handleChange}
                    colorScheme="blue"
                    size="lg"
                  />
                </FormControl>
              </MotionBox>

              <MotionBox 
                variants={slideUpVariants} 
                initial="hidden" 
                animate="visible"
                transition={{ delay: 0.5 }}
              >
                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <Box flex="1" mr={4}>
                    <FormLabel htmlFor="enableSmartClustering" mb="1" fontWeight="600">
                      Enable Smart Clustering
                    </FormLabel>
                    <FormHelperText mt={0} fontSize="xs">
                      Use AI-powered similarity matching for better clustering
                    </FormHelperText>
                  </Box>
                  <Switch 
                    id="enableSmartClustering"
                    name="enableSmartClustering" 
                    isChecked={config.enableSmartClustering} 
                    onChange={handleChange}
                    colorScheme="purple"
                    size="lg"
                  />
                </FormControl>
              </MotionBox>

              <AnimatePresence>
                {config.enableSmartClustering && (
                  <MotionBox
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <FormControl isInvalid={!!errors.minSimilarityScore}>
                      <FormLabel color={labelColor} fontWeight="600">Minimum Similarity Score</FormLabel>
                      <VStack align="stretch" spacing={3}>
                        <Slider
                          value={config.minSimilarityScore}
                          onChange={(val) => handleSliderChange('minSimilarityScore', val)}
                          min={0}
                          max={1}
                          step={0.05}
                        >
                          <SliderTrack bg="gray.200" borderRadius="full">
                            <SliderFilledTrack bg="green.500" />
                          </SliderTrack>
                          <SliderThumb boxSize={6} boxShadow="lg">
                            <Box color="green.500" />
                          </SliderThumb>
                        </Slider>
                        <HStack justify="space-between">
                          <Text fontSize="xs" color="gray.500" fontWeight="600">0.0 (Loose)</Text>
                          <Badge colorScheme="green" fontSize="md" px={4} py={1} borderRadius="lg">
                            {config.minSimilarityScore?.toFixed(2) || '0.60'}
                          </Badge>
                          <Text fontSize="xs" color="gray.500" fontWeight="600">1.0 (Strict)</Text>
                        </HStack>
                      </VStack>
                      {errors.minSimilarityScore ? (
                        <FormHelperText color="red.500">{errors.minSimilarityScore}</FormHelperText>
                      ) : (
                        <FormHelperText>Higher values require more similarity between reports.</FormHelperText>
                      )}
                    </FormControl>
                  </MotionBox>
                )}
              </AnimatePresence>

              <MotionBox 
                variants={slideUpVariants} 
                initial="hidden" 
                animate="visible"
                transition={{ delay: 0.6 }}
              >
                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <Box flex="1" mr={4}>
                    <FormLabel htmlFor="autoNotifyUsers" mb="1" fontWeight="600">
                      Auto-Notify Users
                    </FormLabel>
                    <FormHelperText mt={0} fontSize="xs">
                      Notify users when their reports are merged into a cluster
                    </FormHelperText>
                  </Box>
                  <Switch 
                    id="autoNotifyUsers"
                    name="autoNotifyUsers" 
                    isChecked={config.autoNotifyUsers} 
                    onChange={handleChange}
                    colorScheme="teal"
                    size="lg"
                  />
                </FormControl>
              </MotionBox>

              <MotionBox 
                variants={scaleVariants} 
                initial="hidden" 
                animate="visible"
                transition={{ delay: 0.7 }}
              >
                <Alert 
                  status={estimatedImpact.level === 'High' ? 'warning' : 'info'} 
                  borderRadius="xl"
                  variant="left-accent"
                  boxShadow="md"
                >
                  <AlertIcon as={estimatedImpact.level === 'High' ? MdWarning : MdSpeed} />
                  <Box>
                    <AlertTitle fontSize="sm" fontWeight="bold">
                      Estimated Impact: <Badge colorScheme={estimatedImpact.color} ml={2}>{estimatedImpact.level}</Badge>
                    </AlertTitle>
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      {estimatedImpact.description}
                    </Text>
                  </Box>
                </Alert>
              </MotionBox>
            </VStack>
          </ModalBody>
          
          <ModalFooter 
            borderTopWidth="1px" 
            bg={footerBg}
            position="sticky"
            bottom="0"
            zIndex="1"
          >
            <HStack spacing={3} w="100%" justify="space-between">
              <Button 
                variant="ghost" 
                leftIcon={<Icon as={MdRefresh} />} 
                onClick={handleReset}
                size="md"
                borderRadius="lg"
              >
                Reset
              </Button>
              <HStack>
                <Button 
                  variant="ghost" 
                  onClick={onClose}
                  size="md"
                  borderRadius="lg"
                >
                  Cancel
                </Button>
                <Tooltip 
                  label={!hasChanges ? "No changes to save" : ""} 
                  isDisabled={hasChanges}
                >
                  <Button 
                    type="submit" 
                    colorScheme="blue" 
                    isLoading={isLoading}
                    leftIcon={<Icon as={MdSave} />}
                    isDisabled={!hasChanges}
                    size="md"
                    borderRadius="lg"
                    boxShadow="md"
                    _hover={{
                      transform: 'translateY(-2px)',
                      boxShadow: 'lg'
                    }}
                    transition="all 0.2s"
                  >
                    Save Configuration
                  </Button>
                </Tooltip>
              </HStack>
            </HStack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default AdminIssueThresholdModal;